import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import type { SafeZoneBrand } from '@src/safe-zones/types';

const FALLBACK_NAME = SAFE_ZONE_STRINGS.brand.fallbackName;
const FALLBACK_HANDLE = SAFE_ZONE_STRINGS.brand.fallbackHandle;

/**
 * Resolve brand identity from the active story's primary showcase. Returns
 * a stable object — fields fall back to neutral placeholders when the
 * showcase is missing or partially populated.
 */
export function useSafeZoneBrand(): SafeZoneBrand {
  const videoCreator = useVideoCreatorStore();
  const showcase = videoCreator.story?.primaryShowcase;

  return useMemo<SafeZoneBrand>(() => {
    const name = showcase?.title?.trim() || FALLBACK_NAME;
    const logoUrl = showcase?.logo?.url || '';
    const slug = showcase?.slug?.trim();
    const handle = slug ? `@${slug}` : FALLBACK_HANDLE;
    return { name, handle, logoUrl };
  }, [showcase?.title, showcase?.logo?.url, showcase?.slug]);
}

const LogoImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: var(--surface-muted, #2a2a2a);
  border-radius: 50%;
`;

interface BrandLogoProps {
  className?: string;
  alt?: string;
}

export const BrandLogo: React.FC<BrandLogoProps> = observer(
  ({ className, alt }) => {
    const brand = useSafeZoneBrand();
    if (!brand.logoUrl) return <LogoPlaceholder className={className} />;
    return (
      <LogoImg
        src={brand.logoUrl}
        alt={alt ?? brand.name}
        className={className}
      />
    );
  },
);

interface BrandTextProps {
  className?: string;
}

export const BrandName: React.FC<BrandTextProps> = observer(({ className }) => {
  const brand = useSafeZoneBrand();
  return <span className={className}>{brand.name}</span>;
});

export const BrandHandle: React.FC<BrandTextProps> = observer(
  ({ className }) => {
    const brand = useSafeZoneBrand();
    return <span className={className}>{brand.handle}</span>;
  },
);

/**
 * Resolve the active story's description (the user-authored summary surfaced
 * in DatoCMS). Returns the trimmed description when present, otherwise
 * falls back to the story title, then to the explicit `fallback` arg.
 *
 * The title fallback exists because not every DatoCMS story has a
 * description set, but the overlay should still paint *something
 * story-specific* before reaching for generic placeholder copy.
 */
export function useStoryDescription(fallback = ''): string {
  const videoCreator = useVideoCreatorStore();
  const story = videoCreator.story;
  const description = story?.description?.trim();
  if (description) return description;
  const title = story?.title?.trim();
  if (title) return title;
  return fallback;
}

interface StoryDescriptionProps {
  /** Used when the story has no description set. */
  fallback?: string;
  className?: string;
}

/**
 * Slot for the active story's description. Renders `fallback` when the
 * story has no description, so overlay chrome never paints an empty box
 * (LinkedIn shorts' bottom caption-preview row always shows *some* copy).
 */
export const StoryDescription: React.FC<StoryDescriptionProps> = observer(
  ({ fallback, className }) => {
    const description = useStoryDescription(fallback);
    return <span className={className}>{description}</span>;
  },
);

/**
 * Resolve the active story's title. Returns the trimmed title when present,
 * otherwise the explicit `fallback` arg. Unlike `useStoryDescription`, this
 * has no secondary fallback — the title slot exists precisely because the
 * description may be missing.
 */
export function useStoryTitle(fallback = ''): string {
  const videoCreator = useVideoCreatorStore();
  const title = videoCreator.story?.title?.trim();
  return title || fallback;
}

interface StoryTitleProps {
  /** Used when the story has no title set. */
  fallback?: string;
  className?: string;
}

/**
 * Slot for the active story's title. Used by overlays that paint a video
 * title row (e.g. YouTube long-form's top title strip). Falls back to
 * `fallback` so the chrome never paints an empty title box.
 */
export const StoryTitle: React.FC<StoryTitleProps> = observer(
  ({ fallback, className }) => {
    const title = useStoryTitle(fallback);
    return <span className={className}>{title}</span>;
  },
);
